import React, { useState, useEffect } from 'react';
import { api, useServer } from '../contexts/ServerContext';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { Plus, Edit, Trash2, Tag, X } from 'lucide-react';

function TagsManager() {
  const { server } = useServer();
  const { user } = useAuth();
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTag, setEditingTag] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    color: '#3B82F6',
    description: ''
  });

  useEffect(() => {
    if (user?.rank === 'superadmin') {
      fetchTags();
    }
  }, [server, user?.rank]);

  // Проверка прав доступа
  if (user?.rank !== 'superadmin') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-theme-primary mb-4">Доступ запрещен</h1>
          <p className="text-gmod-white opacity-60">Только superadmin может управлять тегами</p>
        </div>
      </div>
    );
  }

  const fetchTags = async () => {
    try {
      setLoading(true);
      // console.log('Запрашиваем теги для сервера:', server);
      const response = await api.get(`/api/tags?server=${server}`);
      // console.log('Ответ от API тегов:', response);
      // console.log('Полученные теги:', response.data);
      setTags(response.data.data || response.data || []);
    } catch (error) {
      console.error('Ошибка при получении тегов:', error);
      console.error('Детали ошибки:', error.response?.data);
      console.error('Статус ошибки:', error.response?.status);
      toast.error(`Ошибка при загрузке тегов: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Название тега обязательно');
      return;
    }

    try {
      if (editingTag) {
        await api.put(`/api/tags/${editingTag.id}?server=${server}`, formData);
        toast.success('Тег обновлен');
      } else {
        await api.post(`/api/tags?server=${server}`, formData);
        toast.success('Тег создан');
      }
      
      setShowCreateForm(false);
      setEditingTag(null);
      setFormData({ name: '', color: '#3B82F6', description: '' });
      fetchTags();
    } catch (error) {
      console.error('Ошибка при сохранении тега:', error);
      toast.error(error.response?.data?.error || 'Ошибка при сохранении тега');
    }
  };

  const handleEdit = (tag) => {
    setEditingTag(tag);
    setFormData({
      name: tag.name,
      color: tag.color,
      description: tag.description || ''
    });
    setShowCreateForm(true);
  };

  const handleDelete = async (tagId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот тег? Это действие нельзя отменить.')) {
      return;
    }

    try {
      await api.delete(`/api/tags/${tagId}?server=${server}`);
      toast.success('Тег удален');
      fetchTags();
    } catch (error) {
      console.error('Ошибка при удалении тега:', error);
      toast.error(error.response?.data?.error || 'Ошибка при удалении тега');
    }
  };

  const cancelEdit = () => {
    setShowCreateForm(false);
    setEditingTag(null);
    setFormData({ name: '', color: '#3B82F6', description: '' });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gmod-white">Управление тегами</h2>
        <button
          onClick={() => setShowCreateForm(true)}
          className="bg-theme-primary hover:bg-theme-primary-hover text-gmod-white px-4 py-2 rounded font-semibold flex items-center gap-2 transition-colors"
        >
          <Plus size={16} />
          Создать тег
        </button>
      </div>

      {showCreateForm && (
        <div className="bg-gmod-dark p-6 rounded-2xl shadow-lg border border-gmod-lighterer">
          <h3 className="text-lg font-semibold text-gmod-white mb-4">
            {editingTag ? 'Редактировать тег' : 'Создать новый тег'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gmod-white mb-1">
                Название тега *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 bg-gmod-lighter border border-gmod-lighterer text-gmod-white rounded focus:outline-none focus:ring-2 focus:ring-theme-primary"
                placeholder="Введите название тега"
                maxLength={50}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gmod-white mb-1">
                Цвет
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-12 h-10 border border-gmod-lighterer rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="flex-1 px-3 py-2 bg-gmod-lighter border border-gmod-lighterer text-gmod-white rounded focus:outline-none focus:ring-2 focus:ring-theme-primary"
                  placeholder="#3B82F6"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gmod-white mb-1">
                Описание
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 bg-gmod-lighter border border-gmod-lighterer text-gmod-white rounded focus:outline-none focus:ring-2 focus:ring-theme-primary resize-none"
                placeholder="Описание тега (необязательно)"
                rows={3}
                maxLength={200}
              />
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                className="bg-theme-primary hover:bg-theme-primary-hover text-gmod-white px-4 py-2 rounded font-semibold transition-colors"
              >
                {editingTag ? 'Обновить' : 'Создать'}
              </button>
              <button
                type="button"
                onClick={cancelEdit}
                className="bg-gmod-lighter hover:bg-gmod-lighterer text-gmod-white px-4 py-2 rounded font-semibold transition-colors"
              >
                Отмена
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-gmod-dark rounded-2xl shadow-lg border border-gmod-lighterer">
        <div className="p-6">
          <h3 className="text-lg font-semibold text-gmod-white mb-4">Список тегов</h3>
          
          {!Array.isArray(tags) || tags.length === 0 ? (
            <div className="text-center py-8 text-gmod-white opacity-60">
              <Tag size={48} className="mx-auto mb-4 opacity-40" />
              <p>Теги не созданы</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {Array.isArray(tags) && tags.map((tag) => (
                <div
                  key={tag.id}
                  className="flex items-center justify-between p-4 bg-gmod-lighter border border-gmod-lighterer rounded-lg hover:bg-gmod-lighterer transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: tag.color }}
                    ></div>
                    <div>
                      <h4 className="font-medium text-gmod-white">{tag.name}</h4>
                      {tag.description && (
                        <p className="text-sm text-gmod-white opacity-60">{tag.description}</p>
                      )}
                      <p className="text-xs text-gmod-white opacity-40">
                        Создан: {new Date(tag.createdAt).toLocaleDateString('ru-RU')}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(tag)}
                      className="p-2 text-gmod-white opacity-60 hover:text-theme-primary hover:bg-gmod-lighterer rounded transition-colors"
                      title="Редактировать"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(tag.id)}
                      className="p-2 text-gmod-white opacity-60 hover:text-theme-primary hover:bg-gmod-lighterer rounded transition-colors"
                      title="Удалить"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TagsManager; 